import { PermissionReminder } from '@/mobile/PhoneSetup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { captureOriginClick, ORIGINS, OriginProvider } from '@/lib/origin';
import { useScrollRestoration } from '@/lib/scrollRestore';
import { FEED_ANCHOR, FEED_KEEP_MS } from './Feed';
import { HelpButton } from '@/components/help/HelpButton';
import { useMedia, useNarrow } from '@/lib/useMedia';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import clsx from 'clsx';
import { ArrowDown, ArrowLeftRight, ArrowUp, Check, GripVertical, LayoutGrid, Plus, RotateCcw, X } from 'lucide-react';
import { joinRails, moveItem, SIZE_LABEL, SIZE_SPAN, splitRails, WIDGET_BY_KEY, WIDGETS, type LayoutItem, type RailSide, type WidgetSize } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { TZ } from '@/lib/env';
import { useMe, useWorkspaceId } from '@/lib/hooks';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Button, Modal, Spinner, ask } from '@/components/ui';
import { Widget } from './widgets';
import { TemplatesMenu } from './Templates';
import { HomeFeed, HomeShareButtons } from './Feed';
import { BackgroundPicker, Tinted } from './widgetColours';

const SPAN: Record<number, string> = { 1: 'lg:col-span-1', 2: 'lg:col-span-2', 3: 'lg:col-span-3' };

/** Picker for widgets not yet on the page. */
export function AddWidgets({ open, onClose, available, items, onAdd }: { open: boolean; onClose: () => void; available: string[]; items: LayoutItem[]; onAdd: (k: LayoutItem) => void }) {
  const on = new Set(items.map(i => i.key));
  const options = WIDGETS.filter(w => available.includes(w.key) && !on.has(w.key));
  return (
    <Modal open={open} onClose={onClose} title="Add a widget" wide>
      {!options.length ? <p className="text-sm text-slate-500">Every widget available to you is already on your page.</p> : (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="widget-picker">
          {options.map(w => (
            <button key={w.key} onClick={() => onAdd({ key: w.key, size: w.default_size })} className="rounded-lg border border-slate-200 p-3 text-left hover:border-accent-line dark:border-slate-700">
              <div className="flex items-center justify-between"><span className="font-medium">{w.title}</span><Plus className="h-4 w-4 text-slate-500" /></div>
              <div className="text-xs text-slate-500">{w.description}</div>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

/** The editable grid, shared by the home page and admin role defaults. */
export function WidgetGrid({ items, setItems, editing, render }: { items: LayoutItem[]; setItems: (x: LayoutItem[]) => void; editing: boolean; render: (it: LayoutItem) => React.ReactNode }) {
  const [drag, setDrag] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3" data-testid="widget-grid">
      {items.map((it, i) => {
        const def = WIDGET_BY_KEY.get(it.key)!;
        return (
          <div key={it.key} className={clsx('min-w-0', SPAN[SIZE_SPAN[it.size]], editing && 'rounded-xl ring-2 ring-dashed ring-slate-300 dark:ring-slate-700', over === i && drag !== null && drag !== i && 'ring-accent-line')}
            draggable={editing} onDragStart={e => { setDrag(i); e.dataTransfer.effectAllowed = 'move'; }} onDragEnd={() => { setDrag(null); setOver(null); }}
            onDragOver={e => { if (drag !== null) { e.preventDefault(); setOver(i); } }} onDrop={e => { e.preventDefault(); if (drag !== null) setItems(moveItem(items, drag, i)); setDrag(null); setOver(null); }}
            data-widget={it.key}>
            {editing && (
              <div className="flex flex-wrap items-center gap-1 rounded-t-xl bg-slate-100 px-2 py-1.5 text-xs dark:bg-slate-800">
                <GripVertical className="h-4 w-4 cursor-grab text-slate-500" aria-hidden />
                <span className="mr-auto font-medium">{def.title}</span>
                {def.sizes.length > 1 && (
                  <span className="inline-flex overflow-hidden rounded-md border border-slate-300 dark:border-slate-600" role="group" aria-label={`Size of ${def.title}`}>
                    {def.sizes.map(s => (
                      <button key={s} onClick={() => setItems(items.map((x, j) => (j === i ? { ...x, size: s as WidgetSize } : x)))} aria-pressed={it.size === s}
                        className={clsx('px-2 py-0.5', it.size === s ? 'bg-ink text-white' : 'bg-white hover:bg-slate-50 dark:bg-slate-900')}>{SIZE_LABEL[s]}</button>
                    ))}
                  </span>
                )}
                <button onClick={() => setItems(moveItem(items, i, i - 1))} disabled={i === 0} className="rounded p-1 hover:bg-white disabled:opacity-30 dark:hover:bg-slate-700" aria-label={`Move ${def.title} earlier`}><ArrowUp className="h-3.5 w-3.5" /></button>
                <button onClick={() => setItems(moveItem(items, i, i + 1))} disabled={i === items.length - 1} className="rounded p-1 hover:bg-white disabled:opacity-30 dark:hover:bg-slate-700" aria-label={`Move ${def.title} later`}><ArrowDown className="h-3.5 w-3.5" /></button>
                <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="rounded p-1 hover:bg-white dark:hover:bg-slate-700" aria-label={`Remove ${def.title}`}><X className="h-3.5 w-3.5" /></button>
              </div>
            )}
            <div className={clsx('@container', editing && 'pointer-events-none select-none p-2 opacity-80')}>{render(it)}</div>
          </div>
        );
      })}
    </div>
  );
}


/** Rails render widgets at their smallest size; they adapt to the rail width through container queries. */
const railSize = (it: LayoutItem): WidgetSize => { const d = WIDGET_BY_KEY.get(it.key)!; return d.sizes.includes('s') ? 's' : d.sizes[0]; };
type Drag = { side: RailSide; i: number } | null;

/**
 * One side rail of the three-column Home (t131u). While customising, widgets drag within and between rails
 * (drop on a widget to go before it, or on the rail's end zone), with arrow and "other side" buttons as keyboard fallbacks.
 */
function Rail({ side, items, rails, setRails, editing, drag, setDrag, otherLabel }: {
  side: RailSide; items: LayoutItem[]; rails: Record<RailSide, LayoutItem[]>; setRails: (r: Record<RailSide, LayoutItem[]>) => void; editing: boolean;
  drag: Drag; setDrag: (d: Drag) => void; otherLabel: string | null;
}) {
  const [over, setOver] = useState<number | null>(null);
  const other: RailSide = side === 'left' ? 'right' : 'left';
  const drop = (to: number) => {
    if (!drag) return;
    const from = rails[drag.side];
    const moving = from[drag.i];
    const next = { ...rails, [drag.side]: from.filter((_, j) => j !== drag.i) } as Record<RailSide, LayoutItem[]>;
    const target = [...next[side]];
    const at = drag.side === side && drag.i < to ? to - 1 : to;
    target.splice(Math.min(at, target.length), 0, moving);
    next[side] = target;
    setRails(next); setDrag(null); setOver(null);
  };
  const move = (i: number, d: number) => setRails({ ...rails, [side]: moveItem(items, i, i + d) });
  const toOther = (i: number) => setRails({ ...rails, [side]: items.filter((_, j) => j !== i), [other]: [...rails[other], items[i]] } as Record<RailSide, LayoutItem[]>);
  if (!editing && !items.length) return null;
  return (
    <div className="space-y-4" data-testid={`home-rail-${side}`} aria-label={side === 'left' ? 'Left widgets' : 'Right widgets'}>
      {items.map((it, i) => {
        const def = WIDGET_BY_KEY.get(it.key)!;
        return (
          <div key={it.key} data-widget={it.key} draggable={editing}
            onDragStart={e => { setDrag({ side, i }); e.dataTransfer.effectAllowed = 'move'; }} onDragEnd={() => { setDrag(null); setOver(null); }}
            onDragOver={e => { if (drag) { e.preventDefault(); setOver(i); } }} onDragLeave={() => setOver(o => (o === i ? null : o))} onDrop={e => { e.preventDefault(); drop(i); }}
            className={clsx('min-w-0', editing && 'rounded-xl ring-2 ring-dashed ring-slate-300 dark:ring-slate-700', over === i && drag && 'ring-accent-line', over === i && drag && 'border-t-4 border-accent-line')}>
            {editing && (
              <div className="flex items-center gap-1 rounded-t-xl bg-slate-100 px-2 py-1.5 text-xs dark:bg-slate-800">
                <GripVertical className="h-4 w-4 cursor-grab text-slate-500" aria-hidden />
                <span className="mr-auto truncate font-medium">{def.title}</span>
                <button onClick={() => move(i, -1)} disabled={i === 0} className="rounded p-1 hover:bg-white disabled:opacity-30 dark:hover:bg-slate-700" aria-label={`Move ${def.title} up`}><ArrowUp className="h-3.5 w-3.5" /></button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="rounded p-1 hover:bg-white disabled:opacity-30 dark:hover:bg-slate-700" aria-label={`Move ${def.title} down`}><ArrowDown className="h-3.5 w-3.5" /></button>
                <BackgroundPicker k={it.key} title={def.title} />
                {otherLabel && <button onClick={() => toOther(i)} className="rounded p-1 hover:bg-white dark:hover:bg-slate-700" aria-label={`Move ${def.title} to the ${otherLabel}`} title={`Move to the ${otherLabel}`}><ArrowLeftRight className="h-3.5 w-3.5" /></button>}
                <button onClick={() => setRails({ ...rails, [side]: items.filter((_, j) => j !== i) })} className="rounded p-1 hover:bg-white dark:hover:bg-slate-700" aria-label={`Remove ${def.title}`}><X className="h-3.5 w-3.5" /></button>
              </div>
            )}
            <Tinted k={it.key} className={clsx('@container', editing && 'pointer-events-none select-none p-2 opacity-80')}><Widget k={it.key} size={railSize(it)} /></Tinted>
          </div>
        );
      })}
      {editing && (
        <div onDragOver={e => { if (drag) { e.preventDefault(); setOver(items.length); } }} onDrop={e => { e.preventDefault(); drop(items.length); }}
          className={clsx('grid h-16 place-items-center rounded-xl border-2 border-dashed text-xs text-slate-500', over === items.length && drag ? 'border-accent-line bg-accent/10' : 'border-slate-300 dark:border-slate-700')}
          data-testid={`rail-drop-${side}`}>Drop a widget here</div>
      )}
    </div>
  );
}

/** UI review: on phones the things you act on at the door (clock, next shift) come first; the saved order is untouched. */
const PHONE_FIRST = ['clock', 'shifts'];
export function phoneOrder(items: LayoutItem[]): LayoutItem[] {
  const first = PHONE_FIRST.flatMap(k => items.filter(i => i.key === k));
  return [...first, ...items.filter(i => !PHONE_FIRST.includes(i.key))];
}

export function HomePage() {
  const ws = useWorkspaceId();
  const me = useMe();
  const toast = useUi(s => s.toast);
  const q = useQuery({ queryKey: ['home', ws, 'layout'], queryFn: () => api.home.layout(ws!), enabled: Boolean(ws) });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<LayoutItem[]>([]);
  const [adding, setAdding] = useState(false);
  const narrow = useNarrow();
  useEffect(() => { if (q.data && !editing) setDraft(q.data.items); }, [q.data, editing]);
  // Settings → My home page links here with ?customise=1.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get('customise') === '1' && q.data) { setDraft(q.data.items); setEditing(true); setParams(p => { p.delete('customise'); return p; }, { replace: true }); }
  }, [params, q.data, setParams]);
  // Coming back from an item opened here (Back, or "Back to Home") lands where you were.
  const scroller = useRef<HTMLDivElement>(null);
  const nav = useNavigate();
  // t158u / t184u: Back returns to the same place in the feed (same loaded pages, kept in the query cache); the Home
  // icon (any fresh visit) starts at the top. Keyed by route, restored after the layout and feed have rendered.
  // t298u: exact - by the card at the top of the view (late comments and photos above it can't move it), saved in the
  // history entry too; the Home link also comes back to the same place within 30 minutes (the feed's pages stay cached).
  useScrollRestoration(scroller, { ready: Boolean(q.data), anchor: FEED_ANCHOR, restoreOnPushWithin: FEED_KEEP_MS });
  const hour = DateTime.now().setZone(TZ).hour;
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  const save = async () => {
    try { queryClient.setQueryData(['home', ws, 'layout'], await api.home.saveLayout(ws!, draft)); void queryClient.invalidateQueries({ queryKey: ['home', ws, 'templates'] }); setEditing(false); toast('Home page saved', 'success'); }
    catch (e) { toast(errorMessage(e), 'error'); }
  };
  const reset = async () => {
    if (!await ask('Go back to the default home page for your role?')) return;
    try { queryClient.setQueryData(['home', ws, 'layout'], await api.home.resetLayout(ws!)); void queryClient.invalidateQueries({ queryKey: ['home', ws, 'templates'] }); setEditing(false); toast('Reset to the default', 'success'); }
    catch (e) { toast(errorMessage(e), 'error'); }
  };

  const rails = splitRails(editing ? draft : q.data?.items ?? []);
  const setRails = (r: Record<RailSide, LayoutItem[]>) => setDraft(joinRails(r));
  const [drag, setDrag] = useState<Drag>(null);
  // xl+: left rail | feed | right rail. lg: feed | one rail with both sides. Phones: Feed / Widgets switch, door widgets first.
  const wide = useMedia('(min-width: 1280px)');
  const [phoneTab, setPhoneTab] = useState<'feed' | 'widgets'>('feed');
  const doorFirst = phoneOrder(q.data?.items ?? []).filter(i => i.key === 'clock' || i.key === 'shifts');

  return (
    <OriginProvider origin={ORIGINS.home}>
    <div ref={scroller} className="scroll-thin h-full overflow-y-auto bg-slate-50 dark:bg-slate-950" onClickCapture={e => { if (!editing) captureOriginClick(e, nav); }} data-testid="home-scroll">
      <div className="mx-auto max-w-[1680px] px-4 py-5 sm:px-6">
        {/* Phones: greeting and ? share the first row and the buttons wrap below. */}
        <div className="mb-5 flex flex-wrap items-end gap-3">
          <div className="order-1 min-w-0 flex-1">
            <h1 className="text-2xl font-semibold">{greeting}, {me.data?.profile.display_name.split(' ')[0] ?? ''} 👋</h1>
            <p className="text-sm text-slate-500">{DateTime.now().setZone(TZ).toFormat('cccc d LLLL yyyy')}</p>
          </div>
          {/* t643u: computer only: Give kudos and New announcement, centred between the greeting and Layout. */}
          {!narrow && !editing && <div className="order-2 flex flex-1 justify-center">{ws && <HomeShareButtons />}</div>}
          {editing ? (
            <div className="order-3 flex basis-full flex-wrap items-center gap-2 sm:order-2 sm:basis-auto">
              <Button variant="secondary" onClick={() => setAdding(true)}><Plus className="h-4 w-4" />Add widget</Button>
              {q.data?.source === 'mine' && <Button variant="ghost" onClick={() => void reset()}><RotateCcw className="h-4 w-4" />Reset to default</Button>}
              <Button variant="ghost" onClick={() => { setEditing(false); setDraft(q.data?.items ?? []); }}>Cancel</Button>
              <Button onClick={() => void save()}><Check className="h-4 w-4" />Done</Button>
            </div>
          ) : (
            <>
              <div className="order-3 flex basis-full flex-wrap items-center gap-2 sm:order-2 sm:basis-auto">
                {ws && <TemplatesMenu ws={ws} />}
                <Button variant="ghost" onClick={() => { setEditing(true); if (narrow) setPhoneTab('widgets'); }} disabled={!q.data}><LayoutGrid className="h-4 w-4" />Customise</Button>
              </div>
              <HelpButton className="order-2 sm:order-3" />
            </>
          )}
        </div>
        {!editing && <PermissionReminder desktop />}
        {editing && <p className="mb-4 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300">Drag widgets to reorder them or move them to the other side (or use the arrows), pick a background with the palette, and remove what you don't need. Only you see your layout and colours.</p>}
        {q.isLoading ? <Spinner /> : narrow ? (
          <div data-testid="home-narrow">
            {!editing && (
              <div className="mb-4 inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-sm dark:border-slate-700 dark:bg-slate-900" role="tablist" aria-label="Home view">
                {(['feed', 'widgets'] as const).map(t => (
                  <button key={t} role="tab" aria-selected={phoneTab === t} onClick={() => setPhoneTab(t)}
                    className={clsx('rounded-md px-4 py-1.5 font-medium', phoneTab === t ? 'bg-ink text-white dark:bg-white dark:text-ink' : 'text-slate-600 dark:text-slate-300')}>{t === 'feed' ? 'Feed' : 'Widgets'}</button>
                ))}
              </div>
            )}
            {phoneTab === 'feed' && !editing ? (
              <>
                {doorFirst.length > 0 && <div className="mb-4 space-y-4">{doorFirst.map(it => <Tinted key={it.key} k={it.key} className="@container"><Widget k={it.key} size={railSize(it)} /></Tinted>)}</div>}
                <HomeFeed />
              </>
            ) : (
              <Rail side="left" items={editing ? draft.map(i => ({ ...i, col: 'left' as const })) : phoneOrder(q.data?.items ?? [])} rails={{ left: draft, right: [] }}
                setRails={r => setDraft(r.left.map(i => { const old = draft.find(d => d.key === i.key); return old?.col ? { ...i, col: old.col } : i; }))} editing={editing} drag={drag} setDrag={setDrag} otherLabel={null} />
            )}
          </div>
        ) : (
          <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(280px,340px)] xl:grid-cols-[minmax(260px,320px)_minmax(0,1fr)_minmax(280px,340px)]" data-testid="home-columns">
            {wide && <aside className="min-w-0"><Rail side="left" items={rails.left} rails={rails} setRails={setRails} editing={editing} drag={drag} setDrag={setDrag} otherLabel="right" /></aside>}
            <main className="min-w-0 max-w-3xl justify-self-center xl:w-full"><HomeFeed shareRow={false} /></main>
            <aside className="min-w-0 space-y-4">
              {!wide && <Rail side="left" items={rails.left} rails={rails} setRails={setRails} editing={editing} drag={drag} setDrag={setDrag} otherLabel="other side on wide screens" />}
              <Rail side="right" items={rails.right} rails={rails} setRails={setRails} editing={editing} drag={drag} setDrag={setDrag} otherLabel={wide ? 'left' : 'other side on wide screens'} />
            </aside>
          </div>
        )}
        {!editing && q.data && !q.data.items.length && <p className="mt-4 text-center text-sm text-slate-500">You have no widgets. Press Customise to add some.</p>}
        <AddWidgets open={adding} onClose={() => setAdding(false)} available={q.data?.available ?? []} items={draft} onAdd={w => { setDraft([...draft, { ...w, col: 'right' }]); setAdding(false); }} />
      </div>
    </div>
    </OriginProvider>
  );
}
